import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Typography } from '@mui/material';
import CustomButton from 'src/core/ui/CustomButton';
import { CustomField } from 'src/core/forms/CustomField';
import { ReviewTextField } from 'src/core/forms/FieldReview';
import QrCodeIcon from '@mui/icons-material/QrCode';

export const useScannerConfiguration = () => {
  const [open, setOpen] = useState(false);
  return { openDialog: () => setOpen(true), dialogProps: { open, onClose: () => setOpen(false) } };
};

export const ScannerButton = ({ onClick, variant = 'contained' }: { onClick: () => void; variant?: 'contained' | 'outlined' | 'text' }) => <CustomButton title="Configure Barcode Reader" variant={variant} icon={<QrCodeIcon />} onClick={onClick} />;

// Mount alongside the page/card, never inside PageHeader's grouped menu.
export const ScannerDialog = ({ open, onClose }: ScannerDialogProps) => {
  const titleId = useRef(`scanner-dialog-title-${Math.random().toString(36).slice(2)}`);
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby={titleId.current} onKeyDown={(event) => event.stopPropagation()} onKeyUp={(event) => event.stopPropagation()} onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); }}>
    <DialogTitle id={titleId.current}>Configure Barcode Reader</DialogTitle>
    {open && <ScannerGuide />}
    <DialogActions><Box sx={{ width: 'fit-content' }}><CustomButton title="Close" variant="text" onClick={onClose} /></Box></DialogActions>
  </Dialog>;
};

// Exact Code 128 module widths decoded from the supplied images, including FNC3,
// code-set switches, checksums and stop symbols. Plain-text re-encoding loses FNC3.
const barcodeWidths = { "original-enable": "2112143111232131132131311323111313212311311222311143113123112331112", "enable": "2112141143111131412312121214211221322113132331112", "disable": "2112141143111131412312121214211212231111432331112", "test-1234": "2112321122321311231212412331112" };

const BAR_HEIGHT = 30;

const barcodeImage = (widths: string) => {
  let x = 10;
  const bars = Array.from(widths, (digit, index) => {
    const width = Number(digit);
    const bar = index % 2 === 0 ? `<rect x="${x}" y="5" width="${width}" height="${BAR_HEIGHT}"/>` : '';
    x += width;
    return bar;
  }).join('');
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${x + 10} ${BAR_HEIGHT + 10}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="white"/><g fill="black">${bars}</g></svg>`)}`;
};

const barcodeImages = { original: barcodeImage(barcodeWidths['original-enable']), enable: barcodeImage(barcodeWidths.enable), disable: barcodeImage(barcodeWidths.disable), test: barcodeImage(barcodeWidths['test-1234']) };

const Barcode = ({ source, label }: { source: string; label: string }) => <Box component="img" src={source} alt={label} sx={{ display: 'block', width: '100%', maxWidth: 420, height: 'auto', mx: 'auto', bgcolor: '#fff' }} />;

const Step = ({ title, children, barcode }: { title: string; children?: ReactNode; barcode?: ReactNode }) => <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.2fr' }, gap: 2, alignItems: 'center', py: 2, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography fontWeight={600}>{title}</Typography>
    {children}
  </Box>
  {barcode}
</Box>;

const ScannerGuide = () => {
  const [family, setFamily] = useState<ScannerFamily>('paired');
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<TestStatus>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef('');
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const messages: Record<Exclude<TestStatus, 'idle'>, { severity: 'success' | 'info' | 'warning'; text: string }> = {
    on: { severity: 'success', text: 'Works! 1234 was sent automatically.' },
    off: { severity: 'info', text: 'Got 1234 without Enter. Auto-send is off.' },
    wrong: { severity: 'warning', text: `Got "${value}" instead of 1234. Click the field and scan again.` }
  };
  return <DialogContent dividers>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', pb: 1 }}>
      <Typography sx={{ flex: 1, minWidth: 220 }}>Make the barcode reader press Enter after each scan, so scans are sent automatically.</Typography>
      <CustomField options={{ name: 'scannerFamily', label: 'Barcode reader type', select: true, topMargin: '0' }} stateProps={{ state: family, setState: setFamily }} sx={{ minWidth: 240, width: { xs: '100%', sm: 'auto' }, my: 0 }}>
        <MenuItem value="paired">Type A (116613 / 116603)</MenuItem>
        <MenuItem value="original">Type B (VSUFCR)</MenuItem>
      </CustomField>
    </Box>
    <Step title="Step 1. Scan to turn on auto-send" barcode={<Barcode source={family === 'paired' ? barcodeImages.enable : barcodeImages.original} label="Turn on auto-send" />} />
    <Step title="Step 2. Click the field and scan 1234" barcode={<Barcode source={barcodeImages.test} label="Test barcode 1234" />}>
      {/* The reader test needs the actual input ref, focus selection and same-event Enter value. */}
      <ReviewTextField inputRef={inputRef} size="small" fullWidth placeholder="Scan here" value={value} autoComplete="off" inputProps={{ 'aria-label': 'Test barcode reader' }} onFocus={(event) => event.target.select()} onChange={(event) => {
        const next = event.target.value;
        valueRef.current = next;
        setValue(next);
        setStatus('idle');
        window.clearTimeout(timer.current);
        if (next) timer.current = window.setTimeout(() => setStatus(next === '1234' ? 'off' : 'wrong'), 500);
      }} onKeyDown={(event) => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat) return;
        window.clearTimeout(timer.current);
        const input = inputRef.current;
        const currentValue = valueRef.current !== value ? valueRef.current : input?.value ?? value;
        // React can restore the previous controlled value between change and Enter in one batch.
        if (input && input.value !== currentValue) input.value = currentValue;
        valueRef.current = currentValue;
        setValue(currentValue);
        setStatus(currentValue === '1234' ? 'on' : 'wrong');
        input?.select();
      }} />
      {status !== 'idle' && <Alert severity={messages[status].severity} role="status">{messages[status].text}</Alert>}
    </Step>
    <Step title="Undo (optional). Scan to turn off auto-send" barcode={family === 'paired' ? <Barcode source={barcodeImages.disable} label="Turn off auto-send" /> : undefined}>{family === 'original' && <Typography variant="body2" color="text.secondary">No undo code for this type. Use the barcode reader manual.</Typography>}</Step>
  </DialogContent>;
};

type ScannerFamily = 'paired' | 'original';

type TestStatus = 'idle' | 'on' | 'off' | 'wrong';

type ScannerDialogProps = { open: boolean; onClose: () => void };
